import { useEffect, type RefObject } from "react";

type Args = {
    /** When false, the listener is not attached. Owned by callers that hand off Esc to a child surface. */
    enabled: boolean;
    /** Invoked on Escape when no other surface owns the key. */
    onClose: () => void;
    /** True when a modal portal owns Escape (e.g. the source-quote PDF modal in Slice 8). */
    isModalOpen: boolean;
    /** True while an inline row edit is active and owns Escape to cancel its own input. */
    isInlineEditing: boolean;
    /** The panel's outer node. Listener is attached here, not on `document`, to avoid clashing with other surfaces. */
    targetRef: RefObject<HTMLElement | null>;
};

export function usePanelEscape({
    enabled,
    onClose,
    isModalOpen,
    isInlineEditing,
    targetRef,
}: Args) {
    useEffect(() => {
        if (!enabled) return;
        const node = targetRef.current;
        if (!node) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            if (isModalOpen) return;
            if (isInlineEditing) return;
            onClose();
        };
        node.addEventListener("keydown", onKeyDown);
        return () => node.removeEventListener("keydown", onKeyDown);
    }, [enabled, onClose, isModalOpen, isInlineEditing, targetRef]);
}
